import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

export interface ProviderConfig {
  provider: string;
  label: string | null;
  status: string;
  credentials: Record<string, string>; // masked, e.g. { apiKey: "••••f6d2" }
  updated_at: string;
}

export interface ProviderConfigResponse {
  tenant_id: string;
  location_id: string | null;
  active_card_provider: string;
  providers: ProviderConfig[];
}

export function usePaymentProviderConfig(tenantId?: string, locationId?: string | null) {
  return useQuery({
    queryKey: ['admin', 'payment-providers', tenantId, locationId ?? 'tenant-wide'],
    queryFn: () =>
      api.get<ProviderConfigResponse>('/admin/payment-providers', {
        tenant_id: tenantId!,
        ...(locationId ? { location_id: locationId } : {}),
      }),
    enabled: !!tenantId,
  });
}

export interface SaveProviderConfigInput {
  tenant_id: string;
  location_id?: string | null;
  provider: string;
  credentials: Record<string, string>;
  label?: string;
  set_as_route?: boolean;
}

export function useSavePaymentProviderConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SaveProviderConfigInput) => api.post('/admin/payment-providers', input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'payment-providers'] });
    },
  });
}

export function useDeletePaymentProviderConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      tenantId,
      locationId,
      provider,
    }: {
      tenantId: string;
      locationId?: string | null;
      provider: string;
    }) => {
      const params = new URLSearchParams({ tenant_id: tenantId, provider });
      if (locationId) params.set('location_id', locationId);
      return api.delete(`/admin/payment-providers?${params.toString()}`);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'payment-providers'] });
    },
  });
}

// ── API clients (client-credentials auth) ──

export interface ApiClient {
  id: string;
  client_id: string;
  scopes: string[];
  label: string | null;
  status: string;
  created_at: string;
  last_used_at: string | null;
}

export interface CreatedApiClient extends ApiClient {
  client_secret: string; // returned once
  token_url: string;
}

export function usePaymentApiClients(tenantId?: string, locationId?: string | null) {
  return useQuery({
    queryKey: ['admin', 'payment-api-clients', tenantId, locationId ?? 'tenant-wide'],
    queryFn: () =>
      api.get<{ data: ApiClient[] }>('/admin/payment-api-clients', {
        tenant_id: tenantId!,
        ...(locationId ? { location_id: locationId } : {}),
      }),
    enabled: !!tenantId,
  });
}

export function useCreatePaymentApiClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { tenant_id: string; location_id?: string | null; label?: string }) =>
      api.post<CreatedApiClient>('/admin/payment-api-clients', input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'payment-api-clients'] });
    },
  });
}

export function useRevokePaymentApiClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/admin/payment-api-clients/${id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'payment-api-clients'] });
    },
  });
}
